import React, { useState, useEffect, useRef } from "react";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import WrapTextIcon from "@mui/icons-material/WrapText";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import Editor from "@monaco-editor/react";
import { configureSyntaxValidation, problemsLabel, useEditorProblems } from "../code/editor-problems";
import Modal from "./modal";
import {
  textViewerSubject,
  closeTextViewer,
  type TextViewerConfig,
} from "../../services/text-viewer-service";
import "./json-viewer-modal.scss";
import "./text-viewer-modal.scss";

const DEFAULT_WIDTH = 1000;
const DEFAULT_HEIGHT = 680;

function formatSize(chars: number): string {
  return chars < 1024 ? `${chars} chars` : `${(chars / 1024).toFixed(1)} KB`;
}

/**
 * Modal component for reading a large block of text or code in a read-only
 * Monaco editor (highlighting, folding, find, minimap).
 * Triggers from RxJS subject textViewerSubject.
 */
export const TextViewerModal: React.FC = () => {
  const [config, setConfig] = useState<TextViewerConfig | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [wordWrap, setWordWrap] = useState(false);
  // The editor's text; differs from config.text only when the viewer is an editor.
  const [text, setText] = useState("");
  const [isMaximized, setIsMaximized] = useState(false);
  const [modalPosition, setModalPosition] = useState({
    left: "50%",
    top: "50%",
  });
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const centerFor = (w: number, h: number) => ({
    left: `${Math.max(0, (window.innerWidth - w) / 2)}px`,
    top: `${Math.max(0, (window.innerHeight - h) / 2)}px`,
  });

  const defaultSize = () => ({
    width: Math.min(DEFAULT_WIDTH, window.innerWidth - 24),
    height: Math.min(DEFAULT_HEIGHT, window.innerHeight - 24),
  });

  useEffect(() => {
    const subscription = textViewerSubject.subscribe((newConfig) => {
      setConfig(newConfig);
      setText(newConfig?.text ?? "");
      setIsMaximized(false); // always open at the default size
      const { width, height } = defaultSize();
      setModalPosition(centerFor(width, height));
    });

    return () => subscription.unsubscribe();
  }, []);

  const editable = Boolean(config?.onApply);
  // Applying is a change when the text changed, or when the cell is NULL: Apply on an
  // empty editor then stores an empty string, which is not NULL.
  const dirty = editable && (text !== config?.text || Boolean(config?.isNull));
  const { problems, onMount, jumpToFirst } = useEditorProblems();
  const problemText = problemsLabel(problems);

  useEffect(() => {
    if (!config) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // Escape closes a viewer; in an editor with typed text it stays the editor's key.
      if (e.key === "Escape" && !dirty) closeTextViewer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [config, dirty]);

  const toggleMaximize = () => {
    setIsMaximized((max) => {
      const next = !max;
      if (next) {
        setModalPosition({ left: "12px", top: "12px" });
      } else {
        const { width, height } = defaultSize();
        setModalPosition(centerFor(width, height));
      }
      return next;
    });
  };

  const handleCopy = () => {
    if (!config) return;

    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedback(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopyFeedback(false);
      }, 2000);
    });
  };

  if (!config) return null;

  const lineCount = text.split("\n").length;
  const apply = () => {
    config.onApply?.(text);
    closeTextViewer();
  };

  const { width, height } = defaultSize();
  const size = isMaximized
    ? {
        width: `${window.innerWidth - 24}px`,
        height: `${window.innerHeight - 24}px`,
      }
    : { width: `${width}px`, height: `${height}px` };

  return (
    <>
      {/* Click-away backdrop: clicking outside the panel closes the viewer. */}
      <div
        className="json-viewer-backdrop"
        // A stray click outside must not throw away typed text.
        onMouseDown={dirty ? undefined : closeTextViewer}
      />
      <Modal
        // Remount when toggling maximize so the new initialSize is applied (the
        // Modal sets size from initialSize on mount and then mutates the DOM).
        key={isMaximized ? "max" : "normal"}
        id="text-viewer-modal"
        header={
          <div className="json-viewer-header">
            <span className="json-viewer-title">{config.title}</span>
            <span className="json-viewer-info">
              {config.language} · {lineCount} lines ·{" "}
              {formatSize(text.length)}
              {config.isNull && text === "" ? " · NULL" : dirty ? " · edited" : ""}
            </span>
            {problemText && (
              <button
                type="button"
                className={`text-viewer-problems${problems.errors > 0 ? " text-viewer-problems-error" : ""}`}
                onClick={jumpToFirst}
                title={`${problems.summary}\n\nClick to go to the first one.`}
              >
                ⚠ {problemText}
              </button>
            )}
            <div className="json-viewer-header-actions">
              <button
                type="button"
                className={`json-viewer-hdr-btn${wordWrap ? " text-viewer-hdr-btn-on" : ""}`}
                onClick={() => setWordWrap((w) => !w)}
                title={wordWrap ? "Word wrap: on" : "Word wrap: off"}
                aria-label="Toggle word wrap"
                aria-pressed={wordWrap}
              >
                <WrapTextIcon fontSize="small" />
              </button>
              <button
                type="button"
                className="json-viewer-hdr-btn"
                onClick={handleCopy}
                title={copyFeedback ? "Copied!" : "Copy to clipboard"}
                aria-label="Copy to clipboard"
              >
                {copyFeedback ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </button>
              {editable && config.onSetNull && (
                <button
                  type="button"
                  className="json-viewer-hdr-btn text-viewer-null-btn"
                  onClick={() => {
                    config.onSetNull?.();
                    closeTextViewer();
                  }}
                  title="Set the cell to NULL; it is saved with the grid's other changes"
                >
                  Set to NULL
                </button>
              )}
              {editable && (
                <button
                  type="button"
                  className="json-viewer-hdr-btn text-viewer-apply-btn"
                  onClick={apply}
                  disabled={!dirty}
                  title={
                    problemText
                      ? `Apply the edit to the cell as it is, with ${problemText}`
                      : "Apply the edit to the cell; it is saved with the grid's other changes"
                  }
                >
                  Apply
                </button>
              )}
              <button
                type="button"
                className="json-viewer-hdr-btn"
                onClick={closeTextViewer}
                title={dirty ? "Close without applying" : "Close"}
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </button>
              <button
                type="button"
                className="json-viewer-hdr-btn"
                onClick={toggleMaximize}
                title={isMaximized ? "Restore" : "Maximize"}
                aria-label={isMaximized ? "Restore" : "Maximize"}
              >
                {isMaximized ? (
                  <FullscreenExitIcon fontSize="small" />
                ) : (
                  <FullscreenIcon fontSize="small" />
                )}
              </button>
            </div>
          </div>
        }
        hideClose
        onClose={closeTextViewer}
        scrollContent={false}
        resizable={true}
        dragCallback={(pos) =>
          setModalPosition({
            left: `${pos.left}px`,
            top: `${pos.top}px`,
          })
        }
        initialSize={size}
        initialPosition={modalPosition}
        modalOnly={false}
        bodyClassNames={["text-viewer-body"]}
      >
        <div className="text-viewer-container">
          <Editor
            height="100%"
            language={config.language}
            value={text}
            onChange={(v) => setText(v ?? "")}
            theme="vs-dark"
            beforeMount={configureSyntaxValidation}
            onMount={onMount}
            options={{
              readOnly: !editable,
              // a read-only viewer underlines problems too
              renderValidationDecorations: "on",
              domReadOnly: !editable,
              wordWrap: wordWrap ? "on" : "off",
              minimap: { enabled: lineCount > 100 },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              fontSize: 13,
              renderWhitespace: "none",
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default TextViewerModal;
